import Fuse from "fuse.js";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { LuPlusCircle } from "react-icons/lu";
import { cn } from "~/utils/fontsConfig";
import { BASE_INPUT_STYLES } from "../v3/themed/BASE_STYLE_DEFINITIONS";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "./Command";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Separator } from "./Separator";
import { PopoverPortal } from "@radix-ui/react-popover";
import { If } from "../v3/Conditional";

interface MultiSelectProps {
    title?: string;
    placeholder?: string;
    maxBadgesLimit?: number;
    onChange: (values: string[]) => void;
    hasSelectAll?: boolean;
    className?: string;
    options: Array<{
        label: string;
        value: string;
        icon?: React.ComponentType<{ className?: string }>;
    }>;
    showCounts?: boolean;
}

const MultiSelectComponent: React.ForwardRefRenderFunction<
    HTMLButtonElement,
    MultiSelectProps
> = (
    {
        title,
        maxBadgesLimit = 5,
        onChange,
        hasSelectAll,
        className,
        options,
        showCounts = false,
    },
    ref,
) => {
    const isInitialMount = useRef(true);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const fuse = new Fuse(options, {
        keys: ["label"],
        threshold: 0.3,
    });

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return; // skip the effect on mount
        }
        onChange(selectedValues);
    }, [selectedValues]);

    const filteredOptions =
        searchQuery === ""
            ? options
            : fuse.search(searchQuery).map((result) => result.item);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button ref={ref} className={cn(BASE_INPUT_STYLES, className)}>
                    <LuPlusCircle className="mr-2 h-4 w-4 shrink-0" />
                    {title}
                    {selectedValues?.length > 0 && (
                        <>
                            <Separator
                                orientation="vertical"
                                className="mx-2 h-4"
                            />
                            <If condition={showCounts ?? false}>
                                <div className="mr-2 flex items-center rounded-lg bg-v3-N-400 px-2 py-1 text-sm">
                                    {selectedValues.length}
                                </div>
                            </If>
                            <div className="flex flex-wrap gap-1">
                                {selectedValues.length > maxBadgesLimit ? (
                                    <div className="flex items-center rounded-lg bg-v3-N-400 px-2 py-1 text-sm">
                                        {selectedValues.length} selected
                                    </div>
                                ) : (
                                    options
                                        .filter(
                                            (option) =>
                                                selectedValues.indexOf(
                                                    option.value,
                                                ) !== -1,
                                        )
                                        .map((option) => (
                                            <div
                                                key={option.value}
                                                className="flex items-center rounded-lg bg-v3-N-400 px-2 py-1 text-sm"
                                            >
                                                <span>{option.label}</span>
                                            </div>
                                        ))
                                )}
                            </div>
                        </>
                    )}
                </button>
            </PopoverTrigger>
            {/* <PopoverPortal> */}
            <PopoverContent className="h-[300px] w-[200px] p-0" align="start">
                <Command>
                    <CommandInput
                        placeholder={title}
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        {hasSelectAll && (
                            <>
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() =>
                                            setSelectedValues(
                                                options.map((o) => o.value),
                                            )
                                        }
                                        className="justify-center text-center"
                                    >
                                        Select All
                                    </CommandItem>
                                </CommandGroup>
                                <CommandSeparator />
                            </>
                        )}
                        <CommandGroup>
                            {filteredOptions.map((option) => {
                                const isSelected =
                                    selectedValues.indexOf(option.value) !== -1;
                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => {
                                            if (isSelected) {
                                                setSelectedValues((sv) =>
                                                    sv.filter(
                                                        (s) =>
                                                            s !== option.value,
                                                    ),
                                                );
                                            } else {
                                                setSelectedValues((sv) => [
                                                    ...sv,
                                                    option.value,
                                                ]);
                                            }
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                        />
                                        {option.icon && (
                                            <option.icon className="mr-2 h-4 w-4 text-middleground" />
                                        )}
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedValues.length > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() => setSelectedValues([])}
                                        className="justify-center text-center"
                                    >
                                        Clear selection
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
            {/* </PopoverPortal> */}
        </Popover>
    );
};

export const MultiSelect = React.forwardRef(MultiSelectComponent);
